import React from 'react';
import { 
  Select, 
  MenuItem, 
  FormControl, 
  Box,
  Typography,
  Checkbox
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../theme';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';

/**
 * Reusable dropdown filter component with configurable styling and behavior
 * 
 * @param {Object} props
 * @param {string} props.value - Current selected value
 * @param {function} props.onChange - Callback when selection changes
 * @param {Array} props.options - Array of options for the dropdown
 * @param {string} props.valueKey - Key to use for option values
 * @param {string} props.labelKey - Key to use for option labels
 * @param {string} props.colorKey - Key to use for option colors (optional)
 * @param {string} props.defaultLabel - Label for the default "all" option
 * @param {string} props.width - Width of the dropdown
 * @param {Object} props.sx - Additional Material UI sx styles
 */
const FilterSelector = ({
  value = 'all',
  onChange,
  options = [],
  valueKey = 'value',
  labelKey = 'label',
  colorKey = null,
  defaultLabel = "--",
  width = '220px',
  sx = {},
  ...rest
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleChange = (event) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  // Get the display label for the current value
  const getDisplayLabel = () => {
    if (value === 'all') return defaultLabel;
    const selectedOption = options.find(opt => opt[valueKey] === value);
    return selectedOption ? selectedOption[labelKey] : defaultLabel;
  };

  return (
    <FormControl sx={{ width, ...sx }}>
      <Select
        value={value}
        onChange={handleChange}
        displayEmpty
        IconComponent={KeyboardArrowDownIcon}
        renderValue={() => getDisplayLabel()}
        MenuProps={{
          PaperProps: {
            elevation: 2,
            sx: { 
              maxHeight: 300,
              mt: 0.5
            }
          }
        }}
        sx={{
          height: '40px',
          border: `1px solid ${colors.grey[300]}`,
          borderRadius: '4px',
          backgroundColor: theme.palette.mode === 'dark' ?"background.card": '#fff',
          '&:hover': {
            border: `1px solid ${colors.grey[400]}`,
          },
          '& .MuiOutlinedInput-notchedOutline': { 
            border: 'none' 
          },
          '& .MuiSelect-select': {
            paddingLeft: 1.5
          }
        }}
        {...rest}
      >
        {/* Default "all" option */}
        <MenuItem value="all" dense>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox 
              checked={value === 'all'} 
              size="small"
              checkedIcon={<CheckIcon fontSize="small" />}
              sx={{ p: 0.5, mr: 1 }}
            />
            <Typography>{defaultLabel}</Typography>
          </Box>
        </MenuItem>
        
        {/* Option items */}
        {options.map((option, index) => {
          const optionValue = option[valueKey];
          const optionLabel = option[labelKey];
          const optionColor = colorKey && option[colorKey] ? option[colorKey] : null;
          
          // Generate a unique key for each menu item using index as a fallback
          const itemKey = `${optionValue !== undefined ? optionValue : ''}-${index}`;
          
          return (
            <MenuItem key={itemKey} value={optionValue} dense>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox 
                  checked={value === optionValue} 
                  size="small"
                  checkedIcon={<CheckIcon fontSize="small" />}
                  sx={{ p: 0.5, mr: 1 }}
                />
                {optionColor && (
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: optionColor,
                      mr: 1
                    }}
                  />
                )}
                <Typography>{optionLabel || 'Unnamed option'}</Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default FilterSelector;
